import type { TagStatus, TagVariant } from './Tag';
import type { StrictSpacingStyle } from '../util/types';

import { useTheme } from '../ThemeProvider';

import React, { useMemo } from 'react';
import { StyleSheet, View, type ViewProps } from 'react-native';

export interface TagDotProps extends ViewProps {
    /**
     * The status color of the Tag.
     */
    status?: TagStatus;

    /**
     * The variant of the Tag.
     */
    variant?: TagVariant;

    /**
     * Defines the padding style overrides.
     */
    sx?: StrictSpacingStyle;
}

export function TagDot({
    status = 'neutral',
    variant = 'light',
    sx,
}: TagDotProps): React.JSX.Element {
    const theme = useTheme();
    const variantStyle = useMemo(() => {
        if (variant === 'solid') {
            return { backgroundColor: theme.colors.foregroundInverse };
        }
        switch (status) {
            case 'success':
                return { backgroundColor: theme.colors.foregroundStatusSuccess };
            case 'alert':
                return { backgroundColor: theme.colors.foregroundStatusCritical };
            case 'warning':
                return { backgroundColor: theme.colors.foregroundStatusWarning };
            case 'neutral':
                return { backgroundColor: theme.colors.foregroundStatusGeneric };
            case 'info':
                return { backgroundColor: theme.colors.foregroundStatusInfo };
            case 'ai':
                return { backgroundColor: theme.colors.foregroundStatusInfo };
        }
    }, [status, variant, theme.colors]);

    return <View style={[styles.circle, variantStyle, sx]} />;
}

const styles = StyleSheet.create({
    circle: {
        width: 8,
        height: 8,
        borderRadius: 4,
    },
});
